import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AUDIO_DEBOUNCE_MS,
  audioProfiles,
} from "../ui/conference-settings-modal/conference-settings.constants";
import { syncMixBus } from "@features/appointment/synchronized-audio/lib/sync-mix-bus";

/**
 * Применение настроек звука конференции (constraint'ы микрофона, устройство)
 * и громкости микрофона.
 *
 * Громкость применяется гейном в ЖИВОМ контексте шины синхронного аудио
 * (`syncMixBus.setVoiceVolume`) — мгновенно и надёжно во всех браузерах.
 * Эквалайзер удалён: его цепочка строилась на клоне трека в отдельном
 * AudioContext и на WebKit работала нестабильно (ломая в том числе громкость).
 */
export const useConferenceSettingsAudio = ({
  audioSettings,
  setAudioSettings,
  microphoneVolume,
  selectedAudioInputDeviceId,
  selectedProfile,
  setSelectedProfile,
  signalSettingsForm,
  isOpen,
  supportedConstraints,
  localParticipant,
  microphoneTrack,
  isMicrophoneEnabled,
}) => {
  const [isApplyingAudio, setIsApplyingAudio] = useState(false);
  const [audioSettingsError, setAudioSettingsError] = useState("");
  const isFirstAudioSyncRef = useRef(true);

  const hasAdvancedAudioSettings =
    supportedConstraints.autoGainControl ||
    supportedConstraints.echoCancellation ||
    supportedConstraints.noiseSuppression ||
    supportedConstraints.channelCount ||
    supportedConstraints.latency ||
    supportedConstraints.sampleRate ||
    supportedConstraints.sampleSize;

  const audioOptions = useMemo(() => {
    const options = {};

    if (supportedConstraints.autoGainControl) {
      options.autoGainControl = audioSettings.autoGainControl;
    }
    if (supportedConstraints.echoCancellation) {
      options.echoCancellation = audioSettings.echoCancellation;
    }
    if (supportedConstraints.noiseSuppression) {
      options.noiseSuppression = audioSettings.noiseSuppression;
    }
    if (supportedConstraints.channelCount && audioSettings.channelCount !== "") {
      options.channelCount = Number(audioSettings.channelCount);
    }
    if (supportedConstraints.latency && audioSettings.latency !== "") {
      options.latency = Number(audioSettings.latency);
    }
    if (supportedConstraints.sampleRate && audioSettings.sampleRate !== "") {
      options.sampleRate = Number(audioSettings.sampleRate);
    }
    if (supportedConstraints.sampleSize && audioSettings.sampleSize !== "") {
      options.sampleSize = Number(audioSettings.sampleSize);
    }

    return options;
  }, [audioSettings, supportedConstraints]);

  // Громкость микрофона — сразу в шину синхронного аудио, без всяких цепочек.
  // Применяем при открытии модалки (восстановление сохранённого значения)
  // и на каждое движение ползунка. Дебаунс не нужен: запись gain.value — дёшево.
  useEffect(() => {
    if (!isOpen) return;
    if (syncMixBus.isActive?.()) {
      syncMixBus.setVoiceVolume?.(microphoneVolume / 100);
    }
  }, [isOpen, microphoneVolume]);

  const applyAudioSettings = useCallback(async () => {
    try {
      setIsApplyingAudio(true);
      setAudioSettingsError("");

      const nextAudioOptions = {
        ...audioOptions,
        ...(selectedAudioInputDeviceId
          ? {
              // Без exact: если сохранённое устройство пропало (Bluetooth-гарнитура и т.п.),
              // браузер молча откатится на default вместо OverconstrainedError
              deviceId: selectedAudioInputDeviceId,
            }
          : {}),
      };

      // Активна шина синхронного аудио: «микрофонной» публикацией является
      // микс-трек — restartTrack на нём убил бы микс (музыка пропала бы из
      // эфира). Рестартим СЫРОЙ трек микрофона и возвращаем его в шину.
      if (syncMixBus.isActive?.()) {
        const rawVoiceTrack = syncMixBus.getRawVoiceTrack?.();
        if (!rawVoiceTrack) {
          setAudioSettingsError("Не найден трек микрофона. Примените настройки после включения микрофона.");
          return;
        }

        try {
          await rawVoiceTrack.restartTrack(nextAudioOptions);
        } catch (error) {
          try {
            await rawVoiceTrack.restartTrack(
              selectedAudioInputDeviceId ? { deviceId: selectedAudioInputDeviceId } : undefined,
            );
          } catch (restoreError) {
            setAudioSettingsError(
              restoreError?.message || "Не удалось восстановить микрофон. Проверьте доступ к устройству.",
            );
            return;
          }
        }

        // Новый сырой захват — сразу в шину (голос не пропадает ни на секунду)
        syncMixBus.setVoiceTrack(rawVoiceTrack.mediaStreamTrack);
        return;
      }

      if (microphoneTrack?.track) {
        try {
          await microphoneTrack.track.restartTrack(nextAudioOptions);
        } catch (error) {
          // restartTrack сначала останавливает текущий трек и только потом пересоздаёт его
          // через getUserMedia. Если getUserMedia падает (устаревший deviceId, занятое
          // устройство, неподдерживаемые констрейнты), микрофон остаётся выключенным —
          // восстанавливаем его рестартом с минимальными констрейнтами.
          try {
            await microphoneTrack.track.restartTrack(
              selectedAudioInputDeviceId ? { deviceId: selectedAudioInputDeviceId } : undefined,
            );
          } catch (restoreError) {
            setAudioSettingsError(
              restoreError?.message || "Не удалось восстановить микрофон. Проверьте доступ к устройству.",
            );
            return;
          }

          throw error;
        }
      } else if (isMicrophoneEnabled) {
        await localParticipant.setMicrophoneEnabled(true, nextAudioOptions);
      } else {
        setAudioSettingsError("Микрофон выключен. Настройки будут применены после включения.");
      }
    } catch (error) {
      setAudioSettingsError(error?.message || "Не удалось применить настройки звука.");
    } finally {
      setIsApplyingAudio(false);
    }
  }, [
    audioOptions,
    isMicrophoneEnabled,
    localParticipant,
    microphoneTrack,
    selectedAudioInputDeviceId,
  ]);

  const applyProfile = (profileKey) => {
    setSelectedProfile(profileKey);
    const nextAudioSettings = { ...audioProfiles[profileKey].settings };
    setAudioSettings(nextAudioSettings);
    signalSettingsForm.reset(nextAudioSettings);
    setAudioSettingsError("");
  };

  useEffect(() => {
    const subscription = signalSettingsForm.watch((value, { name }) => {
      if (!name) {
        return;
      }

      setAudioSettings((prev) => ({
        ...prev,
        [name]: value[name],
      }));
    });

    return () => subscription.unsubscribe();
  }, [signalSettingsForm, setAudioSettings]);

  useEffect(() => {
    if (!isOpen || !hasAdvancedAudioSettings) {
      isFirstAudioSyncRef.current = true;
      return undefined;
    }
    if (isFirstAudioSyncRef.current) {
      isFirstAudioSyncRef.current = false;
      return undefined;
    }

    const timerId = setTimeout(() => {
      void applyAudioSettings();
    }, AUDIO_DEBOUNCE_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [
    applyAudioSettings,
    audioSettings,
    hasAdvancedAudioSettings,
    isMicrophoneEnabled,
    isOpen,
    selectedAudioInputDeviceId,
  ]);

  return {
    selectedProfile,
    hasAdvancedAudioSettings,
    isApplyingAudio,
    audioSettingsError,
    applyProfile,
  };
};
